import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Header } from "../../../../../components/Header/Header";
import api, { getFreshAccessToken } from "../../../../chat/api";
import { isPlanLimitError } from "../../../../../events/planLimitEvents";
import {
  Search,
  Filter,
  ExternalLink,
  Loader2,
  Sparkles,
  ChevronUp,
  X,
  Check,
  CheckCircle2,
} from "lucide-react";
import toast from "react-hot-toast";
import Checkbox from "../../../../../Checkbox";
import AssignTeammateModal from "../AssignTeammateModal";
import QuestionCard from "./QuestionCard";

import pdfImg from "../../pdf.png";
import xlsImg from "../../xls.png";
import docImg from "../../doc.png";

const SELECTION_LIMIT = 20;

const IndividualQuestionnairePage = () => {
  const { projectId, orgId, docId } = useParams();
  const navigate = useNavigate();

  // State Management
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metaData, setMetaData] = useState({
    fileName: "",
    total: 0,
    answered: 0,
    permission: "viewer",
    userId: null,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedQuestions, setSelectedQuestions] = useState([]);

  // Generation States
  const [generatingAnswers, setGeneratingAnswers] = useState({});
  const [isRegenerating, setIsRegenerating] = useState({});

  // Deleting State
  const [deletingQuestions, setDeletingQuestions] = useState({});
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Bulk Generation State
  const [bulkStatus, setBulkStatus] = useState("idle");

  // Filter State
  const [filterStatus, setFilterStatus] = useState("all");
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Export Dropdown State
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [exportStatus, setExportStatus] = useState("idle");

  // Assign Modal State
  const [assignModalData, setAssignModalData] = useState({
    isOpen: false,
    questionUid: null,
    position: null,
  });

  const filterRef = useRef(null);
  const exportRef = useRef(null);
  const wsRef = useRef(null);

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
      if (exportRef.current && !exportRef.current.contains(event.target)) {
        setIsExportOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch Questions
  const fetchQuestions = useCallback(
    async (isBackground = false) => {
      if (!isBackground) setLoading(true);
      try {
        const response = await api.get(
          `/api/v1/rfp/folders/${projectId}/questions/${docId}/`
        );
        if (response.status === 200) {
          const data = response.data;
          setMetaData({
            fileName: data.document_name || "Untitled Document",
            total: data["total questions"] || 0,
            answered: data["answered questions"] || 0,
            permission: data.permission || "viewer",
            userId: data.user_id || null, // Saved current user ID
          });

          const mappedQuestions = (data.questions || []).map((q) => {
            const parsedSources = (q.source || []).map((src) => ({
              source_chunk: src.source_chunk || src.snippet || "",
              page_number: src.page_number,
              file_url: src.file_url || src.document_url || src.url || "",
              file_name:
                src.file_name || src.document_name || "Unknown Document",
            }));

            return {
              uid: q.uid,
              index: q.index,
              qNumber: `Q.${q.index}`,
              text: q.question_text,
              isAnswered: q.is_answered,
              responseText: q.response_text,
              status: q.is_answered ? "answered" : "pending",
              sources: parsedSources,
              assignedMembers: q.assigned_members || [],
              comments: q.comments || [],
            };
          });

          setQuestions(mappedQuestions);
        }
      } catch (error) {
        console.error("Error fetching questions:", error);
        toast.error("Failed to load questions.");
      } finally {
        if (!isBackground) setLoading(false);
      }
    },
    [projectId, docId]
  );

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  // WebSocket Cleanup
  useEffect(() => {
    return () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
    };
  }, []);

  const triggerGeneration = async (ids, isBulk = false, instruction = null) => {
    if (!ids || ids.length === 0) return;

    if (isBulk) setBulkStatus("generating");
    setGeneratingAnswers((prev) => {
      const newState = { ...prev };
      ids.forEach((id) => (newState[id] = true));
      return newState;
    });

    try {
      const response = await api.post(
        `/api/v1/rfp/questions/generate-response/`,
        {
          folder_id: projectId,
          doc_id: docId,
          question_ids: ids,
          instruction: instruction,
        }
      );

      if (response.status === 200 || response.status === 202) {
        const token = await getFreshAccessToken();
        const wsUrl = process.env.REACT_APP_WS_URL || "ws://localhost:8000";
        const ws = new WebSocket(`${wsUrl}/ws/upload-status/?token=${token}`);
        wsRef.current = ws;

        ws.onmessage = (e) => {
          const data = JSON.parse(e.data);
          if (data.type === "completed" || data.status === "completed") {
            fetchQuestions(true);
            if (isBulk) {
              setBulkStatus("success");
              setSelectedQuestions([]);
              setTimeout(() => setBulkStatus("idle"), 2000);
            } else {
              toast.success("Answer generated successfully!");
            }
            setGeneratingAnswers((prev) => {
              const newState = { ...prev };
              ids.forEach((id) => (newState[id] = false));
              return newState;
            });
            ws.close();
          } else if (data.type === "error" || data.status === "error") {
            toast.error(data.message || "Generation failed");
            if (isBulk) setBulkStatus("idle");
            setGeneratingAnswers((prev) => {
              const newState = { ...prev };
              ids.forEach((id) => (newState[id] = false));
              return newState;
            });
            ws.close();
          }
        };

        ws.onerror = () => {
          toast.error("Connection error. Please refresh.");
          if (isBulk) setBulkStatus("idle");
          setGeneratingAnswers((prev) => {
            const newState = { ...prev };
            ids.forEach((id) => (newState[id] = false));
            return newState;
          });
        };
      } else {
        toast.error(`Unexpected status: ${response.status}`);
        if (isBulk) setBulkStatus("idle");
        setGeneratingAnswers((prev) => {
          const newState = { ...prev };
          ids.forEach((id) => (newState[id] = false));
          return newState;
        });
      }
    } catch (error) {
      if (!isPlanLimitError(error)) {
        toast.error(
          error.response?.data?.message || "Failed to start generation"
        );
      }
      if (isBulk) setBulkStatus("idle");
      setGeneratingAnswers((prev) => {
        const newState = { ...prev };
        ids.forEach((id) => (newState[id] = false));
        return newState;
      });
      if (wsRef.current) wsRef.current.close();
    }
  };

  const handleRegenerate = async (uid, prompt) => {
    setGeneratingAnswers((prev) => ({ ...prev, [uid]: true }));
    setIsRegenerating((prev) => ({ ...prev, [uid]: true }));

    try {
      const response = await api.post(
        "/api/v1/rfp/questions/regenerate-response/",
        {
          folder_id: projectId,
          doc_id: docId,
          question_uid: uid,
          custom_prompt: prompt.toLowerCase(),
        }
      );

      if (response.status === 200 || response.status === 201) {
        toast.success("Response regenerated!");
        await fetchQuestions(true);
      } else {
        throw new Error("Regeneration failed");
      }
    } catch (error) {
      console.error("Regenerate error:", error);
      if (!isPlanLimitError(error)) toast.error(error.response?.data?.message || "Failed to regenerate");
    } finally {
      setGeneratingAnswers((prev) => ({ ...prev, [uid]: false }));
      setIsRegenerating((prev) => ({ ...prev, [uid]: false }));
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const toSelect = filteredQuestions
        .slice(0, SELECTION_LIMIT)
        .map((q) => q.uid);
      setSelectedQuestions(toSelect);
      if (filteredQuestions.length > SELECTION_LIMIT) {
        toast("Selected top 20 questions (Limit reached)", { icon: "ℹ️" });
      }
    } else {
      setSelectedQuestions([]);
    }
  };

  const handleSelectQuestion = (uid) => {
    if (selectedQuestions.includes(uid)) {
      setSelectedQuestions(selectedQuestions.filter((id) => id !== uid));
    } else {
      if (selectedQuestions.length >= SELECTION_LIMIT) {
        toast.error(`You can only select up to ${SELECTION_LIMIT} questions.`);
        return;
      }
      setSelectedQuestions([...selectedQuestions, uid]);
    }
  };

  const handleAssignClick = (e, uid) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setAssignModalData({
      isOpen: true,
      questionUid: uid,
      position: { top: rect.bottom, left: rect.right },
    });
  };

  const handleAssignSubmit = async (selectedUserIds, questionUid = null) => {
    const targetUid = questionUid || assignModalData.questionUid;
    try {
      const response = await api.post(
        `/api/v1/rfp/folders/${projectId}/section-view/assign-members/?questionnaire=true`,
        {
          org_id: parseInt(orgId),
          question_id: targetUid,
          user_ids: selectedUserIds,
        }
      );

      if (response.status === 200 || response.status === 201) {
        await fetchQuestions(true);
        return true;
      } else {
        throw new Error("Failed to assign");
      }
    } catch (error) {
      console.error("Assignment error:", error);
      toast.error("Failed to assign teammates.");
      return false;
    }
  };

  const getFileExtension = (filename) => {
    if (!filename) return "";
    const parts = filename.split(".");
    return parts.length > 1 ? parts.pop().toLowerCase() : "";
  };

  const getFileIcon = (filename) => {
    const ext = getFileExtension(filename);
    if (ext === "pdf") {
      return <img src={pdfImg} alt="PDF" className="w-8 h-9 flex-shrink-0" />;
    } else if (ext === "xls" || ext === "xlsx" || ext === "csv") {
      return <img src={xlsImg} alt="Excel" className="w-8 h-9 flex-shrink-0" />;
    } else if (ext === "doc" || ext === "docx") {
      return <img src={docImg} alt="Word" className="w-8 h-9 flex-shrink-0" />;
    } else {
      return (
        <img src={docImg} alt="Document" className="w-8 h-9 flex-shrink-0" />
      );
    }
  };

  const getFilterLabel = () => {
    if (filterStatus === "answered") return "Answered";
    if (filterStatus === "pending") return "Pending";
    return "All Questions";
  };

  const handleExport = async (format) => {
    let type = "";
    if (format.includes("Word")) type = "docx";
    else if (format.includes("Excel")) type = "xlsx";
    else if (format.includes("PDF")) type = "pdf";
    if (!type) return;

    setIsExportOpen(false);
    setExportStatus("exporting");
    try {
      const response = await api.get(
        `/api/v1/rfp/folders/${docId}/export-questions-responses/?type=${type}`,
        { responseType: "blob" }
      );
      if (response.status === 200) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `${metaData.fileName || "export"}.${type}`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        setExportStatus("success");
        setTimeout(() => setExportStatus("idle"), 2000);
      } else throw new Error("Export failed");
    } catch (error) {
      toast.error("Failed to export answers.");
      setExportStatus("idle");
    }
  };

  const handleRemoveQuestion = async (uid) => {
    setDeletingQuestions((prev) => ({ ...prev, [uid]: true }));
    try {
      await api.post("/api/v1/rfp/questions/delete/", {
        folder_id: projectId,
        doc_id: docId,
        question_ids: [uid],
      });
      toast.success("Question deleted successfully");
      setQuestions((prev) => prev.filter((q) => q.uid !== uid));
      setSelectedQuestions((prev) => prev.filter((id) => id !== uid));
      await fetchQuestions(true);
    } catch (error) {
      toast.error("Failed to delete question");
    } finally {
      setDeletingQuestions((prev) => {
        const newState = { ...prev };
        delete newState[uid];
        return newState;
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedQuestions.length === 0) return;
    setIsBulkDeleting(true);
    try {
      await api.post("/api/v1/rfp/questions/delete/", {
        folder_id: projectId,
        doc_id: docId,
        question_ids: selectedQuestions,
      });
      toast.success(`${selectedQuestions.length} questions removed`);
      setQuestions((prev) =>
        prev.filter((q) => !selectedQuestions.includes(q.uid))
      );
      setSelectedQuestions([]);
      await fetchQuestions(true);
    } catch (error) {
      toast.error("Failed to delete questions");
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const filteredQuestions = questions.filter((q) => {
    const matchesSearch = q.text
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    if (filterStatus === "answered") return matchesSearch && q.isAnswered;
    if (filterStatus === "pending") return matchesSearch && !q.isAnswered;
    return matchesSearch;
  });

//   return (
//     <div className="Contents-Full-Container relative">
//       <AssignTeammateModal
//         isOpen={assignModalData.isOpen}
//         position={assignModalData.position}
//         onClose={() =>
//           setAssignModalData((prev) => ({ ...prev, isOpen: false }))
//         }
//         onAssign={handleAssignSubmit}
//       />

//       <Header
//         icon={"/arrow-back.svg"}
//         name="Go to Questionnaire Manager"
//         onClick={() =>
//           navigate(
//             `/rfp-projects/project/${projectId}/org/${orgId}/questionnaire`
//           )
//         }
//       />

//       <div
//         className="flex flex-col bg-[#F9F9FB] rounded-md font-outfit shadow-sm border border-gray-100"
//         style={{
//           marginLeft: "195px",
//           marginTop: "10px",
//           width: "calc(100% - 205px)",
//           height: "calc(100vh - 100px)",
//           overflow: "hidden",
//         }}
//       >
//         {loading ? (
//           <div className="flex-1 flex flex-col items-center justify-center gap-3 text-[#52525B]">
//             <Loader2 className="w-10 h-10 animate-spin text-[#C95EBE]" />
//             <span className="font-medium text-lg">
//               Hang on, Fetching Questions...
//             </span>
//           </div>
//         ) : (
//           <div className="flex flex-col h-full">
//             <div className="px-5 pt-5 pb-0 flex-shrink-0 z-10 bg-[#F9F9FB]">
//               <div className="bg-white rounded-xl border border-gray-200 px-5 py-3 shadow-[0px_2px_4px_rgba(0,0,0,0.02)]">
//                 <div className="flex justify-between items-center">
//                   <div className="flex gap-4">
//                     <div className="w-8 h-6 mt-2 flex items-center justify-center flex-shrink-0 border border-gray-100">
//                       {getFileIcon(metaData.fileName)}
//                     </div>

//                     <div className="pt-0.5">
//                       <h1 className="text-[16px] font-semibold text-[#18101C] max-w-[300px] truncate">
//                         {metaData.fileName}
//                       </h1>
//                       <p className="text-[13px] text-[#52525B] mt-1">
//                         {metaData.total} Questions{" "}
//                         <span className="mx-1">|</span> {metaData.answered}{" "}
//                         Answered
//                       </p>
//                     </div>
//                   </div>

//                   {selectedQuestions.length > 0 ? (
//                     <div className="flex items-center gap-3 animate-in fade-in duration-200">
//                       <span className="bg-[#8B55DD14] text-[#8B55DD] px-3 py-1.5 rounded-lg text-[13px] font-semibold">
//                         {selectedQuestions.length} Questions Selected
//                       </span>
//                       {metaData.permission !== "reviewer" && (
//                         <button
//                           className="bg-[#E21919BF] hover:bg-[#DC2626] text-white px-4 py-2 rounded-md text-[14px] font-medium transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2"
//                           onClick={handleBulkDelete}
//                           disabled={isBulkDeleting}
//                         >
//                           {isBulkDeleting ? (
//                             <>
//                               <Loader2 size={16} className="animate-spin" />{" "}
//                               Deleting...
//                             </>
//                           ) : (
//                             "Remove Questions"
//                           )}
//                         </button>
//                       )}
//                       <button
//                         disabled={bulkStatus !== "idle" || isBulkDeleting}
//                         className={`flex items-center gap-2 px-4 py-2 text-white text-[14px] font-medium rounded-md transition-all shadow-sm ${
//                           bulkStatus === "success"
//                             ? "bg-green-600 hover:bg-green-700"
//                             : "bg-[#C95EBE] hover:bg-[#A93EA0]"
//                         }`}
//                         onClick={() =>
//                           triggerGeneration(selectedQuestions, true)
//                         }
//                       >
//                         {bulkStatus === "generating" ? (
//                           <>
//                             <Loader2 size={16} className="animate-spin" />{" "}
//                             Generating...
//                           </>
//                         ) : bulkStatus === "success" ? (
//                           <>
//                             <CheckCircle2 size={16} /> Generated
//                           </>
//                         ) : (
//                           <>
//                             <Sparkles size={16} fill="white" /> Generate Answers
//                           </>
//                         )}
//                       </button>
//                       <button
//                         onClick={() => setSelectedQuestions([])}
//                         className="p-2 hover:bg-gray-100 text-gray-400 rounded-full"
//                         disabled={isBulkDeleting}
//                       >
//                         <X size={20} />
//                       </button>
//                     </div>
//                   ) : (
//                     <div className="relative" ref={exportRef}>
//                       <button
//                         onClick={() => setIsExportOpen(!isExportOpen)}
//                         disabled={exportStatus !== "idle"}
//                         className={`flex items-center gap-2 px-4 py-2 text-white text-[14px] font-medium rounded-md transition-colors ${
//                           exportStatus === "success"
//                             ? "bg-green-600"
//                             : "bg-[#C95EBE]"
//                         }`}
//                       >
//                         {exportStatus === "exporting" ? (
//                           <>
//                             <Loader2 size={16} className="animate-spin" />{" "}
//                             Exporting...
//                           </>
//                         ) : exportStatus === "success" ? (
//                           <>
//                             <CheckCircle2 size={16} /> Exported
//                           </>
//                         ) : (
//                           <>
//                             <ExternalLink size={16} /> Export Answers
//                           </>
//                         )}
//                       </button>
//                       {isExportOpen && (
//                         <div className="absolute right-0 top-full mt-2 w-[190px] bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
//                           {["Word (.docx)", "Excel (.xlsx)", "PDF (.pdf)"].map(
//                             (fmt) => (
//                               <button
//                                 key={fmt}
//                                 onClick={() => handleExport(fmt)}
//                                 className="w-full text-left px-4 py-2 text-[14px] text-[#18101C] hover:bg-gray-50 flex items-center gap-3 border-b border-gray-50"
//                               >
//                                 Save as {fmt}
//                               </button>
//                             )
//                           )}
//                         </div>
//                       )}
//                     </div>
//                   )}
//                 </div>

//                 <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
//                   <Checkbox
//                     id="selectAll"
//                     checked={
//                       selectedQuestions.length ===
//                         filteredQuestions.slice(0, SELECTION_LIMIT).length &&
//                       selectedQuestions.length > 0
//                     }
//                     onChange={(checked) =>
//                       handleSelectAll({ target: { checked } })
//                     }
//                     disabled={filteredQuestions.length === 0 || isBulkDeleting}
//                     size="sm"
//                     label={
//                       <span className="text-[14px] font-medium text-[#52525B]">
//                         Select All{" "}
//                         <span className="text-gray-400 font-normal ml-1 text-xs">
//                           (Max: 20)
//                         </span>
//                       </span>
//                     }
//                   />
//                   <div className="flex items-center gap-3">
//                     <div className="relative">
//                       <Search
//                         className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
//                         size={16}
//                       />
//                       <input
//                         type="text"
//                         placeholder="Search Questions..."
//                         value={searchQuery}
//                         onChange={(e) => setSearchQuery(e.target.value)}
//                         className="pl-9 pr-4 py-2 w-[280px] text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:border-[#C95EBE]"
//                       />
//                     </div>
//                     <div className="relative" ref={filterRef}>
//                       <button
//                         onClick={() => setIsFilterOpen(!isFilterOpen)}
//                         className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-[13px] text-[#52525B] font-medium bg-white min-w-[140px] justify-between"
//                       >
//                         <div className="flex items-center gap-2">
//                           <Filter size={16} /> {getFilterLabel()}
//                         </div>
//                         <ChevronUp
//                           size={14}
//                           className={isFilterOpen ? "rotate-0" : "rotate-180"}
//                         />
//                       </button>
//                       {isFilterOpen && (
//                         <div className="absolute right-0 top-full mt-2 w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
//                           {[
//                             { id: "all", label: "All Questions" },
//                             { id: "pending", label: "Pending" },
//                             { id: "answered", label: "Answered" },
//                           ].map((opt) => (
//                             <button
//                               key={opt.id}
//                               onClick={() => {
//                                 setFilterStatus(opt.id);
//                                 setIsFilterOpen(false);
//                               }}
//                               className="w-full text-left px-4 py-2.5 text-[13px] hover:bg-gray-50 flex items-center justify-between"
//                             >
//                               {opt.label}
//                               {filterStatus === opt.id && (
//                                 <Check size={14} className="text-[#C95EBE]" />
//                               )}
//                             </button>
//                           ))}
//                         </div>
//                       )}
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             <div className="flex-1 overflow-y-auto p-5 custom-scrollbar ">
//               {filteredQuestions.length > 0 ? (
//                 <div className="flex flex-col gap-3">
//                   {filteredQuestions.map((q) => (
//                     <QuestionCard
//                       key={q.uid}
//                       q={q}
//                       isSelected={selectedQuestions.includes(q.uid)}
//                       onSelect={handleSelectQuestion}
//                       isGenerating={generatingAnswers[q.uid]}
//                       isRegeneratingThis={isRegenerating[q.uid]}
//                       isDeleting={deletingQuestions[q.uid]}
//                       onDelete={() => handleRemoveQuestion(q.uid)}
//                       onGenerate={() => triggerGeneration([q.uid])}
//                       onRegenerate={(prompt) => handleRegenerate(q.uid, prompt)}
//                       onOpenAssignModal={handleAssignClick}
//                       onAssignSubmit={handleAssignSubmit}
//                       projectId={projectId}
//                       docId={docId}
//                       orgId={orgId}
//                       fetchQuestions={fetchQuestions}
//                       documentPermission={metaData.permission}
//                       currentUserId={metaData.userId}
//                     />
//                   ))}
//                 </div>
//               ) : (
//                 <div className="flex flex-col items-center justify-center py-20 text-center">
//                   <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
//                     <Search className="text-gray-400 w-8 h-8" />
//                   </div>
//                   <h3 className="text-[#18101C] font-semibold">
//                     No questions found
//                   </h3>
//                   <p className="text-[#52525B] text-sm mt-1">
//                     Try adjusting your search terms
//                   </p>
//                 </div>
//               )}
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
return (
  <div className="h-full w-full flex flex-col overflow-hidden bg-[#FEF9F7] relative">
    <AssignTeammateModal
      isOpen={assignModalData.isOpen}
      position={assignModalData.position}
      onClose={() =>
        setAssignModalData((prev) => ({ ...prev, isOpen: false }))
      }
      onAssign={handleAssignSubmit}
    />

    <div className="flex-shrink-0">
      <Header
        icon={"/arrow-back.svg"}
        name="Go to Questionnaire Manager"
        onClick={() =>
          navigate(
            `/rfp-projects/project/${projectId}/org/${orgId}/questionnaire`
          )
        }
      />
    </div>

    <div className="flex-1 min-h-0 w-full px-4 pt-3 pb-4 box-border">
      {/* Removed marginLeft and fixed height inline styles. Let flex handle the layout naturally. */}
      <div className="flex flex-col h-full bg-[#F9F9FB] rounded-md font-outfit shadow-sm border border-gray-100 overflow-hidden min-h-0">
        {loading ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-[#52525B]">
            <Loader2 className="w-10 h-10 animate-spin text-[#C95EBE]" />
            <span className="font-medium text-lg">
              Hang on, Fetching Questions...
            </span>
          </div>
        ) : (
          <div className="flex flex-col h-full overflow-hidden">
            <div className="px-5 pt-5 pb-0 flex-shrink-0 z-10 bg-[#F9F9FB]">
              <div className="bg-white rounded-xl border border-gray-200 px-5 py-3 shadow-[0px_2px_4px_rgba(0,0,0,0.02)]">
                <div className="flex justify-between items-center">
                  <div className="flex gap-4">
                    <div className="w-8 h-6 mt-2 flex items-center justify-center flex-shrink-0 border border-gray-100">
                      {getFileIcon(metaData.fileName)}
                    </div>

                    <div className="pt-0.5">
                      <h1 className="text-[16px] font-semibold text-[#18101C] max-w-[300px] truncate">
                        {metaData.fileName}
                      </h1>
                      <p className="text-[13px] text-[#52525B] mt-1">
                        {metaData.total} Questions{" "}
                        <span className="mx-1">|</span> {metaData.answered}{" "}
                        Answered
                      </p>
                    </div>
                  </div>

                  {selectedQuestions.length > 0 ? (
                    <div className="flex items-center gap-3 animate-in fade-in duration-200">
                      <span className="bg-[#8B55DD14] text-[#8B55DD] px-3 py-1.5 rounded-lg text-[13px] font-semibold">
                        {selectedQuestions.length} Questions Selected
                      </span>
                      {metaData.permission !== "reviewer" && (
                        <button
                          className="bg-[#E21919BF] hover:bg-[#DC2626] text-white px-4 py-2 rounded-md text-[14px] font-medium transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2"
                          onClick={handleBulkDelete}
                          disabled={isBulkDeleting}
                        >
                          {isBulkDeleting ? (
                            <>
                              <Loader2 size={16} className="animate-spin" />{" "}
                              Deleting...
                            </>
                          ) : (
                            "Remove Questions"
                          )}
                        </button>
                      )}
                      <button
                        disabled={bulkStatus !== "idle" || isBulkDeleting}
                        className={`flex items-center gap-2 px-4 py-2 text-white text-[14px] font-medium rounded-md transition-all shadow-sm ${
                          bulkStatus === "success"
                            ? "bg-green-600 hover:bg-green-700"
                            : "bg-[#C95EBE] hover:bg-[#A93EA0]"
                        }`}
                        onClick={() =>
                          triggerGeneration(selectedQuestions, true)
                        }
                      >
                        {bulkStatus === "generating" ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />{" "}
                            Generating...
                          </>
                        ) : bulkStatus === "success" ? (
                          <>
                            <CheckCircle2 size={16} /> Generated
                          </>
                        ) : (
                          <>
                            <Sparkles size={16} fill="white" /> Generate Answers
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setSelectedQuestions([])}
                        className="p-2 hover:bg-gray-100 text-gray-400 rounded-full"
                        disabled={isBulkDeleting}
                      >
                        <X size={20} />
                      </button>
                    </div>
                  ) : (
                    <div className="relative" ref={exportRef}>
                      <button
                        onClick={() => setIsExportOpen(!isExportOpen)}
                        disabled={exportStatus !== "idle"}
                        className={`flex items-center gap-2 px-4 py-2 text-white text-[14px] font-medium rounded-md transition-colors ${
                          exportStatus === "success"
                            ? "bg-green-600"
                            : "bg-[#C95EBE]"
                        }`}
                      >
                        {exportStatus === "exporting" ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />{" "}
                            Exporting...
                          </>
                        ) : exportStatus === "success" ? (
                          <>
                            <CheckCircle2 size={16} /> Exported
                          </>
                        ) : (
                          <>
                            <ExternalLink size={16} /> Export Answers
                          </>
                        )}
                      </button>
                      {isExportOpen && (
                        <div className="absolute right-0 top-full mt-2 w-[190px] bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
                          {["Word (.docx)", "Excel (.xlsx)", "PDF (.pdf)"].map(
                            (fmt) => (
                              <button
                                key={fmt}
                                onClick={() => handleExport(fmt)}
                                className="w-full text-left px-4 py-2 text-[14px] text-[#18101C] hover:bg-gray-50 flex items-center gap-3 border-b border-gray-50"
                              >
                                Save as {fmt}
                              </button>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                  <Checkbox
                    id="selectAll"
                    checked={
                      selectedQuestions.length ===
                        filteredQuestions.slice(0, SELECTION_LIMIT).length &&
                      selectedQuestions.length > 0
                    }
                    onChange={(checked) =>
                      handleSelectAll({ target: { checked } })
                    }
                    disabled={filteredQuestions.length === 0 || isBulkDeleting}
                    size="sm"
                    label={
                      <span className="text-[14px] font-medium text-[#52525B]">
                        Select All{" "}
                        <span className="text-gray-400 font-normal ml-1 text-xs">
                          (Max: 20)
                        </span>
                      </span>
                    }
                  />
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Search
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        size={16}
                      />
                      <input
                        type="text"
                        placeholder="Search Questions..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 pr-4 py-2 w-[280px] text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:border-[#C95EBE]"
                      />
                    </div>
                    <div className="relative" ref={filterRef}>
                      <button
                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                        className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-[13px] text-[#52525B] font-medium bg-white min-w-[140px] justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <Filter size={16} /> {getFilterLabel()}
                        </div>
                        <ChevronUp
                          size={14}
                          className={isFilterOpen ? "rotate-0" : "rotate-180"}
                        />
                      </button>
                      {isFilterOpen && (
                        <div className="absolute right-0 top-full mt-2 w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                          {[
                            { id: "all", label: "All Questions" },
                            { id: "pending", label: "Pending" },
                            { id: "answered", label: "Answered" },
                          ].map((opt) => (
                            <button
                              key={opt.id}
                              onClick={() => {
                                setFilterStatus(opt.id);
                                setIsFilterOpen(false);
                              }}
                              className="w-full text-left px-4 py-2.5 text-[13px] hover:bg-gray-50 flex items-center justify-between"
                            >
                              {opt.label}
                              {filterStatus === opt.id && (
                                <Check size={14} className="text-[#C95EBE]" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-5 custom-scrollbar">
              {filteredQuestions.length > 0 ? (
                <div className="flex flex-col gap-3">
                  {filteredQuestions.map((q) => (
                    <QuestionCard
                      key={q.uid}
                      q={q}
                      isSelected={selectedQuestions.includes(q.uid)}
                      onSelect={handleSelectQuestion}
                      isGenerating={generatingAnswers[q.uid]}
                      isRegeneratingThis={isRegenerating[q.uid]}
                      isDeleting={deletingQuestions[q.uid]}
                      onDelete={() => handleRemoveQuestion(q.uid)}
                      onGenerate={() => triggerGeneration([q.uid])}
                      onRegenerate={(prompt) => handleRegenerate(q.uid, prompt)}
                      onOpenAssignModal={handleAssignClick}
                      onAssignSubmit={handleAssignSubmit}
                      projectId={projectId}
                      docId={docId}
                      orgId={orgId}
                      fetchQuestions={fetchQuestions}
                      documentPermission={metaData.permission}
                      currentUserId={metaData.userId}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center h-full">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <Search className="text-gray-400 w-8 h-8" />
                  </div>
                  <h3 className="text-[#18101C] font-semibold">
                    No questions found
                  </h3>
                  <p className="text-[#52525B] text-sm mt-1">
                    Try adjusting your search terms
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);

};

export default IndividualQuestionnairePage;
